/**
 * Емоції каналу Olsior: твічеві, BTTV, FFZ і 7TV — і канальні, і глобальні.
 *
 * Основа — public/emoted.json, його збирає getEmotes.mjs у корені
 * репозиторію. Поверх нього підмішуємо свіжий набір 7TV із /api/emotes7tv:
 * там емоції міняються між ефірами, і чекати на перезбірку файлу означало б
 * «нової емоції в чаті немає».
 *
 * Вантажимо один раз на весь застосунок і тримаємо і мапою «назва -> картинка»
 * (для підстановки в тексті), і списком (для пошуку та вибору смайла).
 */

const EMOTES_URL = '/emoted.json'
const SEVENTV_URL = '/api/emotes7tv'

// Один запит на вкладку, скільки б компонентів не попросило
let loadPromise = null

// Згадка в тексті: «@нік» і те, що за ним (кома, двокрапка, знак питання).
// Довжина — як у ніках сайту; коротші «@a» частіше виявляються не
// зверненням, а початком чогось іншого
const MENTION = /^@([A-Za-z0-9_.-]{2,32})([^\s]*)$/

// Посилання в тексті. Ловимо тільки «http(s)://…» і «www.…»: голий
// «olsior.tv» посеред речення надто легко сплутати зі скороченням («і т.д.»),
// а помилково підкреслене слово гірше за непідкреслене посилання.
//
// Дужки й лапки перед адресою та розділові знаки після неї лишаються
// текстом: «(https://olsior.tv/live)» — це посилання в дужках, а не адреса,
// що закінчується дужкою
const LINK = /^([([{<«"']*)((?:https?:\/\/|www\.)[^\s]+?)([)\]}>».,!?;:"']*)$/i

/**
 * Розбирає слово-посилання. Повертає null, якщо це не посилання або схема
 * не та: у чат пишуть будь-що, а «javascript:» у href робити не можна
 */
const parseLink = (token) => {
  const match = token.match(LINK)
  if (!match) return null

  let [, before, url, after] = match

  // Закриваюча дужка — частина адреси лише тоді, коли в ній є відкриваюча
  // (посилання на Вікіпедію), інакше це дужка речення
  if (after.startsWith(')') && url.includes('(')) {
    url += ')'
    after = after.slice(1)
  }

  const href = url.startsWith('www.') ? `https://${url}` : url

  let parsed
  try {
    parsed = new URL(href)
  } catch (e) {
    return null
  }

  if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return null
  // Хост без крапки — це не домен, а щось на кшталт «http://localhost» із
  // чужого повідомлення
  if (!parsed.hostname.includes('.')) return null

  return { before, after, link: { href, label: linkLabel(parsed) } }
}

// Скільки адреси показуємо. Повний URL із хвостом параметрів у вузькій
// колонці чату займає три рядки, а читають у ньому все одно домен
const LINK_LABEL = 42

const linkLabel = (parsed) => {
  const tail = `${parsed.pathname}${parsed.search}${parsed.hash}`.replace(/\/$/, '')
  const label = `${parsed.hostname.replace(/^www\./i, '')}${tail}`
  return label.length > LINK_LABEL ? `${label.slice(0, LINK_LABEL - 1)}…` : label
}

// Гіфки з пошуку в чаті (див. backend/src/api/gif) — у повідомленні це
// просто посилання на CDN провайдера, а малюємо його самою гіфкою. Тільки
// ці хости й тільки .gif/.webp: картинкою стає те, що прийшло з пошуку, а не
// будь-яке посилання, яке хтось вставив
const GIF_HOSTS = /^(?:media\d*\.giphy\.com|i\.giphy\.com|media\d*\.tenor\.com|c\.tenor\.com)$/i
const GIF_PATH = /\.(?:gif|webp)$/i

const gifOf = (href) => {
  try {
    const url = new URL(href)
    if (url.protocol !== 'https:' || !GIF_HOSTS.test(url.hostname) || !GIF_PATH.test(url.pathname)) return null
    return url.toString()
  } catch (e) {
    return null
  }
}

export const isGifUrl = (href) => !!gifOf(href)

// Провайдера в статичному файлі немає — та він там і не потрібен був, поки
// емоції тільки підставлялись у текст. Для вибору смайла групувати вже є за
// чим, і host картинки каже це однозначно: у кожного провайдера свій CDN
const PROVIDER_BY_HOST = [
  [/(^|\.)7tv\.app$/i, '7tv'],
  [/(^|\.)betterttv\.net$/i, 'bttv'],
  [/(^|\.)frankerfacez\.com$/i, 'ffz'],
  [/(^|\.)jtvnw\.net$/i, 'twitch']
]

export const PROVIDER_LABELS = {
  '7tv': '7TV',
  bttv: 'BTTV',
  ffz: 'FFZ',
  twitch: 'Twitch',
  other: 'Інші'
}

const providerOf = (url) => {
  try {
    const host = new URL(url).hostname
    const found = PROVIDER_BY_HOST.find(([re]) => re.test(host))
    return found ? found[1] : 'other'
  } catch (e) {
    return 'other'
  }
}

const fetchAll = async () => {
  const [stat, seventv] = await Promise.all([
    $fetch(EMOTES_URL).catch(() => []),
    // Свіжий 7TV — приємний бонус, а не умова роботи чату
    $fetch(SEVENTV_URL).catch(() => [])
  ])

  const list = []
  const seen = new Set()

  // Свіжий набір іде першим: якщо картинка емоції на 7TV змінилась, у чаті
  // має бути нова, а не та, що лежала у файлі з минулого збору
  for (const emote of [...(seventv || []), ...(stat || [])]) {
    if (!emote?.name || !emote.url || seen.has(emote.name)) continue
    seen.add(emote.name)
    list.push({
      name: emote.name,
      url: emote.url,
      provider: emote.provider || providerOf(emote.url)
    })
  }

  return list
}

export const useEmotes = () => {
  const emotes = useState('twitchEmotes', () => null)
  const list = useState('twitchEmotesList', () => [])

  const load = async () => {
    if (!import.meta.client || emotes.value) return

    if (!loadPromise) loadPromise = fetchAll()

    const all = await loadPromise
    list.value = all
    emotes.value = Object.fromEntries(all.map((e) => [e.name, e.url]))
  }

  /**
   * Ріже текст на шматки тексту, емоцій, згадок «@нік» і посилань.
   *
   * Емоція — це ціле слово між пробілами, а не будь-який збіг усередині:
   * інакше «BOOBAlicious» перетворювалося б на картинку з хвостом. Розділювачі
   * лишаємо в потоці, щоб переноси й подвійні пробіли не з'їдалися.
   *
   * Згадку впізнаємо за самим написанням, а не за списком глядачів: списку
   * тих, хто зараз у чаті, тут немає, та й «@когось, кого вже немає в
   * кімнаті» лишається зверненням до людини. Розділові знаки після ніка
   * лишаються текстом — «@olsior,» це звернення й кома, а не нік із комою.
   *
   * @param {string} text
   * @return {Array<{text?: string, emote?: object, mention?: string, link?: object, gif?: string}>}
   */
  const tokenize = (text) => {
    const source = String(text ?? '')
    if (!source) return []

    const map = emotes.value
    const parts = []
    let buffer = ''

    const flush = () => {
      if (!buffer) return
      parts.push({ text: buffer })
      buffer = ''
    }

    for (const token of source.split(/(\s+)/)) {
      const mention = token && token.match(MENTION)
      if (mention) {
        flush()
        parts.push({ mention: mention[1] })
        // Хвіст після ніка («,», «:», «?») — звичайний текст
        if (mention[2]) buffer += mention[2]
        continue
      }

      // Посилання перевіряємо раніше за емоцію: назва емоції — це слово без
      // «://», тож переплутатись вони не можуть, а от адреса, що збіглася зі
      // словом зі списку, мала б лишитись адресою
      const link = token && parseLink(token)
      if (link) {
        if (link.before) buffer += link.before
        flush()
        const gif = gifOf(link.link.href)
        parts.push(gif ? { gif } : { link: link.link })
        if (link.after) buffer += link.after
        continue
      }

      const url = token && map?.[token]
      if (!url) {
        buffer += token
        continue
      }

      flush()
      parts.push({ emote: { name: token, url } })
    }

    flush()
    return parts
  }

  /**
   * Підказки при написанні. Спершу ті, що починаються з набраного, потім
   * ті, де воно всередині: людина набирає початок назви, а не її середину,
   * тож збіг з початку майже завжди і є тим, що шукали
   */
  const search = (query, limit = 20) => {
    const needle = String(query || '').toLowerCase()
    if (!needle) return list.value.slice(0, limit)

    const starts = []
    const inside = []

    for (const emote of list.value) {
      const name = emote.name.toLowerCase()
      if (name.startsWith(needle)) starts.push(emote)
      else if (name.includes(needle)) inside.push(emote)
      if (starts.length >= limit) break
    }

    return [...starts, ...inside].slice(0, limit)
  }

  /**
   * Для панелі вибору: групами по провайдерах, 7TV першим — його в каналі
   * найбільше, і саме його шукають
   */
  const groups = computed(() => {
    const order = ['7tv', 'bttv', 'ffz', 'twitch', 'other']
    const byProvider = new Map()

    for (const emote of list.value) {
      if (!byProvider.has(emote.provider)) byProvider.set(emote.provider, [])
      byProvider.get(emote.provider).push(emote)
    }

    return order
      .filter((id) => byProvider.get(id)?.length)
      .map((id) => ({
        id,
        label: PROVIDER_LABELS[id] || id,
        emotes: byProvider.get(id)
      }))
  })

  return { emotes, list, groups, load, tokenize, search }
}
